import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RequestError } from '../../../interfaces/error.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { GetContainersParams, GetContainersResponse } from '../interfaces/get-containers';
import { Container } from '../../../interfaces/container.interface';
import { GetContainerResponse } from '../interfaces/get-container';
import { GetContainerProductsResponse, ProductStorage } from '../interfaces/get-container-products';

export interface ContainerState {
  containers: {
    ids: string[];
    entities: Record<string, Container>;
  };
  products: {
    ids: string[];
    entities: Record<string, ProductStorage>;
  };
  options: GetContainersParams;
  containerCode: string;
  filterField: string;
  container: Container | null;
  page: number;
  size: number;
  total: number;
  noMore: boolean;
  errorCode: number | null;
}

const initialState: ContainerState = {
  containers: {
    ids: [],
    entities: {},
  },
  products: {
    ids: [],
    entities: {},
  },
  options: { size: 15 }, // Tamaño de página por defecto de 15
  containerCode: '',
  filterField: 'code',
  container: null,
  page: 1,
  size: 100,
  total: 0,
  noMore: false,
  errorCode: null,
};

const containerAdapter = createEntityAdapter<Container>((container: Container) => container.code);
const productAdapter = createEntityAdapter<ProductStorage>((product: ProductStorage) => product.id);

const containerSlice = createSlice({
  name: 'container',
  initialState,
  reducers: {
    getContainersInitApiAction: (state) => {
      state.errorCode = null;
    },
    getContainersSuccessApiAction: (
      state,
      action: PayloadAction<GetContainersResponse>
    ) => {
      containerAdapter.addMany(action.payload.containers, state.containers);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.containers.length < state.size;
    },
    getContainersFailureApiAction: (
      state,
      action: PayloadAction<RequestError>
    ) => {
      state.errorCode = action.payload.errorCode;
    },
    getContainerProductsInitApiAction: (state) => {
      state.errorCode = null;
      // Limpiar productos anteriores al iniciar nueva carga
      productAdapter.removeAll(state.products);
    },
    getContainerProductsSuccessApiAction: (
      state,
      action: PayloadAction<GetContainerProductsResponse>
    ) => {
      productAdapter.addMany(action.payload.product_storages, state.products);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.product_storages.length < state.size;
    },
    getContainerProductsFailureApiAction: (
      state,
      action: PayloadAction<RequestError>
    ) => {
      state.errorCode = action.payload.errorCode;
    },
    getContainerInitApiAction: (state) => {
      state.errorCode = null;
    },
    getContainerSuccessApiAction: (
      state,
      action: PayloadAction<GetContainerResponse>
    ) => {
      state.container = action.payload.container;
    },
    postContainerInitApiAction: (state) => {
      state.errorCode = null;
    },
    postContainerSuccessApiAction: () => {},
    postContainerFailuireApiAction: (
      state,
      action: PayloadAction<RequestError>
    ) => {
      state.errorCode = action.payload.errorCode;
    },
    putContainerInitApiAction: (state) => {
      state.errorCode = null;
    },
    putContainerSuccessApiAction: () => {},
    putContainerFailuireApiAction: (
      state,
      action: PayloadAction<RequestError>
    ) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteContainerInitApiAction: () => {},
    deleteContainerSuccessApiAction: () => {},
    deleteContainerFailuireApiAction: () => {},
    patchContainerLocationInitApiAction: () => {},
    patchContainerLocationSuccessApiAction: () => {},
    patchContainerLocationFailuireApiAction: () => {},
    postProductToContainerInitApiAction: (state) => {
      state.errorCode = null;
    },
    postProductToContainerSuccessApiAction: () => {},
    postProductToContainerFailuireApiAction: (
      state,
      action: PayloadAction<RequestError>
    ) => {
      state.errorCode = action.payload.errorCode;
    },
    setContainerAction: (state, action: PayloadAction<Container | null>) => {
      state.container = action.payload;
    },
    setOptionsAction: (state, action: PayloadAction<GetContainersParams>) => {
      state.options = action.payload;
      containerAdapter.removeAll(state.containers);
      state.page = 1;
    },
    setContainerCodeAction: (state, action: PayloadAction<string>) => {
      state.containerCode = action.payload;
    },
    setFilterFieldAction: (state, action: PayloadAction<string>) => {
      state.filterField = action.payload;
    },
    resetOptionsAction: (state) => {
      state.options = {};
      state.filterField = 'code';
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
    containerClearAction: (state) => {
      containerAdapter.removeAll(state.containers);
      state.total = 0;
      state.page = 1;
      state.container = null;
      state.errorCode = null;
    },
  },
});

export const {
  getContainersInitApiAction,
  getContainersSuccessApiAction,
  getContainersFailureApiAction,
  postContainerInitApiAction,
  postContainerSuccessApiAction,
  postContainerFailuireApiAction,
  putContainerInitApiAction,
  putContainerSuccessApiAction,
  putContainerFailuireApiAction,
  deleteContainerInitApiAction,
  deleteContainerSuccessApiAction,
  deleteContainerFailuireApiAction,
  patchContainerLocationInitApiAction,
  patchContainerLocationSuccessApiAction,
  patchContainerLocationFailuireApiAction,
  getContainerProductsInitApiAction,
  getContainerProductsSuccessApiAction,
  getContainerProductsFailureApiAction,
  postProductToContainerInitApiAction,
  postProductToContainerSuccessApiAction,
  postProductToContainerFailuireApiAction,
  setContainerCodeAction,
  setContainerAction,
  increasePageAction,
  resetPageAction,
  containerClearAction,
  getContainerInitApiAction,
  getContainerSuccessApiAction,
  setOptionsAction,
  setFilterFieldAction,
  resetOptionsAction,
} = containerSlice.actions;

export default containerSlice.reducer;
